import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { Download } from "lucide-react";

import { DeltaCell } from "@/components/cinch/DeltaCell";
import { KpiTile } from "@/components/cinch/KpiTile";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { cn } from "@/lib/utils";

import { fmtCur, fmtDate, fmtMRR, monthLabel } from "../../renewals/format";
import { usePartnerDetail, usePartners } from "../api";
import { exportPartnerWorkbook } from "../export";
import { CHART_COLORS } from "../../renewals/components/dashboard/colors";

import { SubAccountsSection } from "./sub-accounts/SubAccountsSection";

/**
 * Expanded-mode partner view. Renders inside the EntityDrawer when the
 * partners URL carries `?partner=<accountId>&expanded=1`. Prop-driven so it
 * doesn't depend on a specific route — drawer mounts it freely. Reads the
 * partner row out of the cached list (already loaded by the time someone
 * clicks in); only fetches the detail payload (POs / companies / history /
 * sub-links).
 */
export function PartnerDetailContent({ accountId }: { accountId: string }) {
  const list = usePartners();
  const detail = usePartnerDetail(accountId);
  const partner = list.data?.rows.find((p) => p.account_id === accountId);

  if (detail.isLoading || !partner) {
    return <p className="p-5 text-sm text-muted-foreground">Loading partner…</p>;
  }
  if (detail.error) {
    return <p className="p-5 text-sm text-destructive">{String(detail.error)}</p>;
  }
  if (!detail.data) {
    return <p className="p-5 text-sm text-muted-foreground">No detail.</p>;
  }
  const { purchase_orders, companies, billing_history, sub_links } = detail.data;
  const hasSubs = (partner.sub_qb_count ?? 0) > 0;

  return (
    <div className="flex flex-col gap-6 p-5">
      <header className="flex flex-col gap-2">
        <div className="flex flex-wrap items-baseline gap-3">
          <h1 className="text-2xl font-bold tracking-tight">{partner.account_name}</h1>
          <span className="font-mono text-xs text-muted-foreground">QB-{partner.qb_id}</span>
          <PartnerLevelChip level={partner.partner_level} />
        </div>
        <div className="text-sm text-muted-foreground">
          Master <b className="text-foreground">{partner.master_type ?? "—"}</b>
          {partner.master_signed_at && <> · signed {fmtDate(partner.master_signed_at)}</>}
          {partner.master_term_months ? <> · {partner.master_term_months}-mo term</> : null}
          {" · "}industry: {partner.industry_name ?? "—"}
          {" · "}closer: {partner.closer_name ?? "—"}
        </div>
      </header>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
        <KpiTile
          label="Current annualized"
          value={fmtCur(partner.current_arr)}
          sub={
            hasSubs
              ? `${fmtCur(partner.own_arr)} own + ${fmtCur(partner.sub_arr)} subs`
              : `as of ${partner.current_period}`
          }
        />
        <KpiTile
          label="Subscription ARR"
          value={fmtCur(partner.current_subscription_arr)}
          sub="IS_MRR_SUBSCRIPTION rows × 12"
        />
        <KpiTile
          label="Run rate"
          value={fmtCur(partner.run_rate_arr)}
          sub="trailing 3-month avg × 12"
        />
        <KpiTile
          label="ARR 12-mo Δ"
          value={<DeltaCell value={partner.arr_t12m_delta} />}
          sub={partner.period_12mo_ago ? `vs ${partner.period_12mo_ago}` : ""}
        />
      </div>

      <SubAccountsSection
        partner={{
          qb_id: partner.qb_id,
          account_id: partner.account_id,
          account_name: partner.account_name,
        }}
        subLinks={sub_links}
      />

      <Card>
        <CardHeader>
          <CardTitle>Run rate · trailing 3-month avg × 12 (last 24 months)</CardTitle>
          <CardDescription>
            Total billed vs subscription-only ARR. The gap is mailers + ad-hoc revenue.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {billing_history.length === 0 ? (
            <p className="py-4 text-center text-sm text-muted-foreground">
              No billing history for this partner.
            </p>
          ) : (
            <RunRateChart data={billing_history} />
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Purchase orders ({purchase_orders.length})</CardTitle>
          <CardDescription>
            Resold / sub-licensed contracts billing under this partner's master agreement.
          </CardDescription>
        </CardHeader>
        <CardContent className="overflow-auto p-0">
          <table className="w-full text-sm">
            <thead className="text-left text-xs uppercase text-muted-foreground">
              <tr>
                <th className="px-2 py-2">Type</th>
                <th className="px-2 py-2">Sub-customer (PO company)</th>
                <th className="px-2 py-2">Signed</th>
                <th className="px-2 py-2">Effective</th>
                <th className="px-2 py-2 text-right">MRR</th>
                <th className="px-2 py-2 text-right">ARR</th>
              </tr>
            </thead>
            <tbody>
              {purchase_orders.map((p, i) => {
                const isReplacement = p.contract_type_name === "Replacement Purchase Order";
                const mrr = Number(p.mrr_total) || 0;
                return (
                  <tr key={i} className="border-t border-border">
                    <td className="px-2 py-1.5">
                      <Badge variant={isReplacement ? "info" : "neutral"} className="font-mono">
                        {isReplacement ? "Replacement PO" : "PO"}
                      </Badge>
                    </td>
                    <td className="px-2 py-1.5">
                      {String(p.company_name_dba ?? p.company_name ?? "—")}
                    </td>
                    <td className="px-2 py-1.5 text-muted-foreground">
                      {fmtDate(String(p.contract_signed_at ?? ""))}
                    </td>
                    <td className="px-2 py-1.5 text-muted-foreground">
                      {fmtDate(String(p.effective_from ?? ""))}
                      {p.effective_through ? (
                        <> → {fmtDate(String(p.effective_through))}</>
                      ) : null}
                    </td>
                    <td className="px-2 py-1.5 text-right tabular-nums">{fmtMRR(mrr)}</td>
                    <td className="px-2 py-1.5 text-right tabular-nums">{fmtCur(mrr * 12)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Linked companies ({companies.length})</CardTitle>
        </CardHeader>
        <CardContent className="overflow-auto p-0">
          <table className="w-full text-sm">
            <thead className="text-left text-xs uppercase text-muted-foreground">
              <tr>
                <th className="px-2 py-2">Name</th>
                <th className="px-2 py-2">Location</th>
                <th className="px-2 py-2">Active</th>
                <th className="px-2 py-2">Billing range</th>
              </tr>
            </thead>
            <tbody>
              {companies.map((c, i) => (
                <tr key={i} className="border-t border-border">
                  <td className="px-2 py-1.5">{String(c.name ?? "—")}</td>
                  <td className="px-2 py-1.5 text-muted-foreground">
                    {[c.city, c.state].filter(Boolean).join(", ") || "—"}
                  </td>
                  <td className="px-2 py-1.5">
                    {c.is_active ? (
                      <Badge variant="success">Active</Badge>
                    ) : (
                      <Badge variant="neutral">Inactive</Badge>
                    )}
                  </td>
                  <td className="px-2 py-1.5 text-muted-foreground">
                    {String(c.billed_first_date ?? "—")} → {String(c.billed_last_date ?? "—")}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      <details className="rounded-md border border-border bg-muted/30 p-3 text-sm">
        <summary className="cursor-pointer font-medium">
          Show monthly billing-history table
        </summary>
        <div className="mt-3 overflow-auto">
          <table className="w-full text-xs">
            <thead className="text-left uppercase text-muted-foreground">
              <tr>
                <th className="px-2 py-1.5">Period</th>
                <th className="px-2 py-1.5 text-right">Total ARR</th>
                <th className="px-2 py-1.5 text-right">Subscription ARR</th>
                <th className="px-2 py-1.5 text-right">3-mo run rate</th>
              </tr>
            </thead>
            <tbody>
              {billing_history.map((h) => (
                <tr key={h.period} className="border-t border-border">
                  <td className="px-2 py-1.5 font-mono">{monthLabel(h.period + "-01")}</td>
                  <td className="px-2 py-1.5 text-right tabular-nums">{fmtCur(h.total_arr)}</td>
                  <td className="px-2 py-1.5 text-right tabular-nums">
                    {fmtCur(h.subscription_arr)}
                  </td>
                  <td className="px-2 py-1.5 text-right tabular-nums">
                    {fmtCur(h.run_rate_arr)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </details>

      <div className="flex justify-end border-t border-border pt-4">
        <Button
          variant="outline"
          onClick={() => void exportPartnerWorkbook(partner, detail.data!)}
        >
          <Download className="mr-2 h-4 w-4" />
          Export to Excel
        </Button>
      </div>
    </div>
  );
}

function PartnerLevelChip({ level }: { level: string }) {
  // Partner tier — top tier gets power weight, mid tier reads as brand,
  // bottom tier is neutral. Matches PartnersPage's levelClasses.
  const map: Record<string, string> = {
    "Cinch Platinum": "bg-power-light text-power",
    Certified: "bg-brand-light text-brand",
    Growth: "bg-muted text-muted-foreground",
  };
  return (
    <span
      className={cn(
        "rounded-md px-2 py-0.5 text-xs font-bold uppercase tracking-wider",
        map[level] ?? "bg-muted",
      )}
    >
      {level}
    </span>
  );
}

function RunRateChart({
  data,
}: {
  data: Array<{
    period: string;
    total_arr: number;
    subscription_arr: number;
    run_rate_arr: number;
  }>;
}) {
  return (
    <div className="h-64">
      <ResponsiveContainer>
        <LineChart data={data} margin={{ top: 10, right: 16, bottom: 6, left: 0 }}>
          <CartesianGrid stroke={CHART_COLORS.gridLine} strokeDasharray="2 3" vertical={false} />
          <XAxis dataKey="period" tick={{ fontSize: 10 }} tickLine={false} minTickGap={32} />
          <YAxis
            tick={{ fontSize: 10 }}
            tickLine={false}
            axisLine={false}
            tickFormatter={(v) => fmtCur(Number(v))}
            width={70}
          />
          <Tooltip
            formatter={(value, name) => [
              value == null ? "—" : fmtCur(Number(value)),
              String(name),
            ]}
          />
          <Line
            type="monotone"
            dataKey="run_rate_arr"
            name="Run rate (3-mo avg × 12)"
            stroke={CHART_COLORS.power}
            strokeWidth={2.5}
            dot={false}
          />
          <Line
            type="monotone"
            dataKey="subscription_arr"
            name="Subscription ARR (× 12)"
            stroke={CHART_COLORS.success}
            strokeWidth={2}
            dot={false}
          />
          <Legend wrapperStyle={{ fontSize: 11 }} />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}
